import React, { useState } from "react";
import { motion } from "framer-motion";
import { useSubmitSurvey } from "@/api/hooks";
import { Linkedin, Instagram, MessageCircle, Music, Twitter, CheckCircle2, ChevronDown } from "lucide-react";

const SOCIAL_LINKS = [
  { icon: Linkedin, href: "https://www.linkedin.com/company/mom3ntum-io/", label: "LinkedIn" },
  { icon: Instagram, href: "https://www.instagram.com/mom3ntum.fans", label: "Instagram" },
  { icon: MessageCircle, href: "https://discord.gg/mom3ntum", label: "Discord" },
  { icon: Music, href: "https://t.me/mom3ntum", label: "Telegram" },
  { icon: Twitter, href: "https://x.com/mom3ntum_io", label: "X" },
];

const EVENT_OPTIONS = [
  { value: "live_shows", label: "הופעות חיות (אמנים בינלאומיים / מקומיים)" },
  { value: "festivals", label: "פסטיבלים שנתיים (אלקטרוני, רוק, פופ)" },
  { value: "special_sports", label: "אירועי ספורט מיוחדים (טניס, F1, אולימפיאדה)" },
  { value: "football", label: "משחקי כדורגל (ליגת האלופות, ליגות אירופאיות, מונדיאל)" },
  { value: "basketball", label: "משחקי כדורסל (Euroleague, NBA)" },
];

const COMPANION_OPTIONS = [
  { value: "partner", label: "בן/בת הזוג" },
  { value: "friends", label: "חברים" },
  { value: "family", label: "משפחה (כולל ילדים)" },
];

const BUDGET_OPTIONS = [
  { value: "up_to_2000", label: "עד 2,000 ש\u05F4\u05D7" },
  { value: "2000_5000", label: "2,000 - 5,000 ש\u05F4\u05D7" },
  { value: "5000_10000", label: "5,000 - 10,000 ש\u05F4\u05D7" },
  { value: "sky_is_the_limit", label: "Sky is the Limit (חבילות VIP בלבד)" },
];

const DESTINATION_OPTIONS = [
  { value: "europe", label: "לונדון / פריז / ברלין (אירופה הקלאסית)" },
  { value: "usa", label: "ארה\u05F4\u05D1 (ניו יורק, וגאס וכו')" },
  { value: "dubai", label: "דובאי / אבו דאבי" },
  { value: "doesnt_matter", label: "לא משנה לי היעד, משנה לי האירוע!" },
];

function CheckboxGroup({ options, selected, onChange }) {
  const toggle = (value) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  return (
    <div>
      {options.map((opt, i) => {
        const checked = selected.includes(opt.value);
        const icon = checked
          ? "/images/survey/checked_checkbox.svg"
          : "/images/survey/empty_checkbox.svg";
        return (
          <div
            key={opt.value}
            onClick={() => toggle(opt.value)}
            role="checkbox"
            aria-checked={checked}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle(opt.value); } }}
            className="flex items-center gap-3 py-3 cursor-pointer select-none"
            style={i < options.length - 1 ? { borderBottom: "1px solid rgba(255,255,255,0.06)" } : undefined}
          >
            <img src={icon} alt="" className="w-6 h-6 flex-shrink-0" />
            <span className="leading-snug" style={{ color: "#fff", fontSize: "1.05rem" }}>
              {opt.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function RadioGroup({ options, selected, onChange }) {
  return (
    <div>
      {options.map((opt, i) => {
        const checked = selected === opt.value;
        const icon = checked
          ? "/images/survey/checked_radio.svg"
          : "/images/survey/empty_radio.svg";
        return (
          <div
            key={opt.value}
            onClick={() => onChange(opt.value)}
            role="radio"
            aria-checked={checked}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onChange(opt.value); } }}
            className="flex items-center gap-3 py-3 cursor-pointer select-none"
            style={i < options.length - 1 ? { borderBottom: "1px solid rgba(255,255,255,0.06)" } : undefined}
          >
            <img src={icon} alt="" className="w-6 h-6 flex-shrink-0" />
            <span className="leading-snug" style={{ color: "#fff", fontSize: "1.05rem" }}>
              {opt.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function QuestionBlock({ num, title, subtitle, children, delay = 0, showError }) {
  return (
    <motion.div
      id={`question-${num}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="mb-8"
    >
      <div className="flex items-start gap-3 mb-3">
        <span className="font-black flex-shrink-0" style={{ color: "#c8f135", fontSize: "1.3rem" }}>{num}.</span>
        <div>
          <p className="font-bold leading-snug" style={{ color: "#c8f135", fontSize: "1.2rem" }}>{title}</p>
          {subtitle && (
            <p className="mt-1" style={{ color: "#fff", fontSize: "0.95rem" }}>{subtitle}</p>
          )}
        </div>
      </div>
      {children}
      {showError && (
        <p className="mt-2" style={{ color: "#ef4444", fontSize: "0.9rem" }}>* שדה חובה</p>
      )}
    </motion.div>
  );
}

function SuccessState({ fullName }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="text-center py-12 px-4"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        className="mb-6"
      >
        <CheckCircle2 className="w-16 h-16 mx-auto" style={{ color: "#c8f135" }} />
      </motion.div>

      <h2 className="font-black text-white mb-8" style={{ fontSize: "1.5rem" }}>
        תודה, המידע שלך נקלט בהצלחה, בואו לעקוב אחרינו ברשתות להמשך הנחיות ועדכונים
      </h2>

      <div className="flex justify-center gap-5 mb-10">
        {SOCIAL_LINKS.map(({ icon: Icon, href, label }) => (
          <a
            key={href}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center w-12 h-12 rounded-full transition-all duration-200 hover:scale-110"
            style={{ background: "rgba(200,241,53,0.1)", border: "1.5px solid rgba(200,241,53,0.2)" }}
            aria-label={label}
          >
            <Icon className="w-5 h-5" style={{ color: "#c8f135" }} />
          </a>
        ))}
      </div>

      <a
        href="/"
        className="inline-flex items-center gap-2 font-bold px-8 py-3 rounded-full transition-all duration-200 active:scale-95"
        style={{ background: "#c8f135", color: "#000" }}
      >
        למעבר לדשבורד
      </a>
    </motion.div>
  );
}

export default function SurveyForm({ fullName, existing }) {
  const [eventInterests, setEventInterests] = useState(existing?.event_interests || []);
  const [soldOutStory, setSoldOutStory] = useState(existing?.sold_out_story || "");
  const [companions, setCompanions] = useState(existing?.companions || []);
  const [dreamEvent, setDreamEvent] = useState(existing?.dream_event || "");
  const [budgetRange, setBudgetRange] = useState(existing?.budget_range || "");
  const [preferredDestination, setPreferredDestination] = useState(existing?.preferred_destination || "");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const submitSurvey = useSubmitSurvey();

  const [touched, setTouched] = useState(false);

  const missing = [];
  if (eventInterests.length === 0) missing.push(1);
  if (!soldOutStory.trim()) missing.push(2);
  if (companions.length === 0) missing.push(3);
  if (!dreamEvent.trim()) missing.push(4);
  if (!budgetRange) missing.push(5);
  if (!preferredDestination) missing.push(6);

  const canSubmit = missing.length === 0;

  const handleSubmit = () => {
    setTouched(true);
    if (!canSubmit) {
      const firstMissing = missing[0];
      const el = document.getElementById(`question-${firstMissing}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setError("");

    submitSurvey.mutate(
      {
        event_interests: eventInterests,
        sold_out_story: soldOutStory.trim(),
        companions,
        dream_event: dreamEvent.trim(),
        budget_range: budgetRange,
        preferred_destination: preferredDestination,
      },
      {
        onSuccess: () => setSubmitted(true),
        onError: (err) => setError(err.message || "שגיאה בשליחה, נסו שוב"),
      }
    );
  };

  if (submitted) {
    return <SuccessState fullName={fullName} />;
  }

  return (
    <div style={{ background: "#0a0a0a", minHeight: "100vh" }}>
      {/* Hero image */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative w-full overflow-hidden"
        style={{ minHeight: "60vh", maxHeight: "70vh" }}
      >
        <img
          src="/images/survey/hero.jpg"
          alt=""
          className="w-full h-full object-cover absolute inset-0"
        />
        {/* Heading over image */}
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="font-black text-white leading-tight"
            style={{ fontSize: "clamp(1.9rem, 7vw, 2.8rem)" }}
          >
            הצעד הראשון
            <br />
            בדרך לחוויה הבאה שלכם
            <br />
            <span style={{ color: "#c8f135" }}>הושלם!</span>
          </motion.h1>
        </div>
      </motion.div>

      {/* Body copy + questions in constrained container */}
      <div className="max-w-lg mx-auto px-5">
        {/* Body copy */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-center pt-8 pb-6"
        >
          <p className="leading-relaxed mb-3" style={{ color: "#fff", fontSize: "1.05rem" }}>
            איזה כיף שבחרתם להצטרף לקהילת Momentum.
            {" "}אנחנו כבר מתחילים להניע את הגלגלים כדי להחזיר את הכוח לידיים שלכם, המעריצים.
          </p>
          <p className="leading-relaxed mb-3" style={{ color: "#fff", fontSize: "1.05rem" }}>
            כדי שנוכל לדייק את המומנטום עבורכם, להילחם בספסרות במקומות הנכונים ולהשיג לכם גישה הוגנת לאירועים שאתם באמת אוהבים &ndash; אנחנו צריכים להכיר את ה-Vibe שלכם קצת יותר טוב.
          </p>
          <p style={{ color: "#fff", fontSize: "0.95rem" }}>
            זה לוקח פחות מדקה, וזה מה שיבטיח שאתם תהיו הראשונים בתור:
          </p>
        </motion.div>

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mb-8"
        >
          <div className="mb-6" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} />
          <p className="font-bold text-white mb-2" style={{ fontSize: "1.2rem" }}>
            ספרו לנו לאן הלב שלכם מושך
          </p>
          <ChevronDown className="w-5 h-5 mx-auto" style={{ color: "#fff" }} />
        </motion.div>

        {/* Questions */}
        <QuestionBlock
          num={1}
          title="מה עושה לכם את זה? באילו אירועים אתם הכי מתעניינים?"
          subtitle="(ניתן לבחור יותר מאופציה אחת)"
          delay={0.1}
          showError={touched && missing.includes(1)}
        >
          <CheckboxGroup options={EVENT_OPTIONS} selected={eventInterests} onChange={setEventInterests} />
        </QuestionBlock>

        <QuestionBlock
          num={2}
          title="לאיזה אירוע ממש רציתם לרכוש כרטיסים וקיבלתם SOLD OUT (או מחיר הזוי)?"
          delay={0.15}
          showError={touched && missing.includes(2)}
        >
          <textarea
            value={soldOutStory}
            onChange={(e) => setSoldOutStory(e.target.value)}
            placeholder="טקסט חופשי"
            rows={3}
            className="w-full rounded-lg px-4 py-3 resize-none outline-none transition-all duration-150"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.2)",
              color: "#fff",
              fontSize: "1.05rem",
            }}
            onFocus={(e) => { e.target.style.borderColor = "rgba(200,241,53,0.5)"; }}
            onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.2)"; }}
          />
        </QuestionBlock>

        <QuestionBlock num={3} title="עם מי אתם בדרך כלל חוגגים?" delay={0.2} showError={touched && missing.includes(3)}>
          <CheckboxGroup options={COMPANION_OPTIONS} selected={companions} onChange={setCompanions} />
        </QuestionBlock>

        <QuestionBlock
          num={4}
          title="איפה הייתם רוצים להיות עוד השנה? ספרו לנו על הופעה, משחק או פסטיבל מעבר לים שאתם חולמים עליהם."
          delay={0.25}
          showError={touched && missing.includes(4)}
        >
          <textarea
            value={dreamEvent}
            onChange={(e) => setDreamEvent(e.target.value)}
            placeholder="טקסט חופשי"
            rows={3}
            className="w-full rounded-lg px-4 py-3 resize-none outline-none transition-all duration-150"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.2)",
              color: "#fff",
              fontSize: "1.05rem",
            }}
            onFocus={(e) => { e.target.style.borderColor = "rgba(200,241,53,0.5)"; }}
            onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.2)"; }}
          />
        </QuestionBlock>

        <QuestionBlock
          num={5}
          title="מה התקציב הממוצע שלכם לחוויה כזו (כולל טיסה ולינה במידת הצורך)?"
          delay={0.3}
          showError={touched && missing.includes(5)}
        >
          <RadioGroup options={BUDGET_OPTIONS} selected={budgetRange} onChange={setBudgetRange} />
        </QuestionBlock>

        <QuestionBlock
          num={6}
          title="עוד שאלה לטובת דאטה גאוגרפי: האם יש לכם יעד מועדף בחו״ל שבו אתם אוהבים לצרוך את האירועים שלכם?"
          delay={0.35}
          showError={touched && missing.includes(6)}
        >
          <RadioGroup options={DESTINATION_OPTIONS} selected={preferredDestination} onChange={setPreferredDestination} />
        </QuestionBlock>

        {/* Error message */}
        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center mb-4"
            style={{ fontSize: "1.05rem", color: "#ef4444" }}
          >
            {error}
          </motion.p>
        )}

        {/* Submit CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-4 pb-10"
        >
          <button
            onClick={handleSubmit}
            disabled={submitSurvey.isPending}
            className="w-full font-black py-4 rounded-xl transition-all duration-200 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: "#c8f135",
              color: "#000",
              fontSize: "1.15rem",
            }}
          >
            {submitSurvey.isPending ? "שולח..." : "תגשימו לי את החלום \u003C"}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
